import React, { useState } from 'react';
import { RecipeForm, RecipeFormData } from '../components/RecipeForm';
import { MealOptionsDisplay, MealOption } from '../components/MealOptionsDisplay';
import { RecipeDetailView, DetailedRecipe } from '../components/RecipeDetailView';
import { ArrowRight, ArrowLeft, User, Send } from 'lucide-react';
import { UserProfile } from '../App';
import { NavigationDropdown } from '../components/NavigationDropdown';
interface RecipeGeneratorPageProps {
  userProfile: UserProfile | null;
  onEditProfile: () => void;
  onBackToHome?: () => void;
  onNavigateHome: () => void;
  onGenerateRecipe: () => void;
  onImportRecipe: () => void;
  onBrowseRecipes: () => void;
  onManageGuests: () => void;
}
type OptionSet = {
  id: string;
  options: MealOption[];
  agentMessage: string;
  timestamp: Date;
};
export function RecipeGeneratorPage({
  userProfile,
  onEditProfile,
  onBackToHome,
  onNavigateHome,
  onGenerateRecipe,
  onImportRecipe,
  onBrowseRecipes,
  onManageGuests
}: RecipeGeneratorPageProps) {
  const [currentOptions, setCurrentOptions] = useState<MealOption[] | null>(null);
  const [agentMessage, setAgentMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [optionHistory, setOptionHistory] = useState<OptionSet[]>([]);
  const [currentRecipe, setCurrentRecipe] = useState<DetailedRecipe | null>(null);
  const [guestCount, setGuestCount] = useState(2);
  const [currentOptionCount, setCurrentOptionCount] = useState(3);
  const [chatInput, setChatInput] = useState('');
  const [optionsChatInput, setOptionsChatInput] = useState('');
  // Mobile step tracking (1 = form step 1, 2 = form step 2, 3 = form step 3, 4 = options, 5 = recipe)
  const [mobileStep, setMobileStep] = useState(1);
  const generateMockOptions = (count: number, userMessage?: string): {
    options: MealOption[];
    message: string;
  } => {
    const allOptions: MealOption[] = [{
      id: '1',
      title: 'Mediterranean Quinoa Bowl',
      description: 'A vibrant bowl featuring quinoa, roasted vegetables, chickpeas, and a tangy lemon-tahini dressing. Perfect for a light yet satisfying meal.',
      thumbnail: '🥗',
      prepTime: '25 min',
      difficulty: 'Easy'
    }, {
      id: '2',
      title: 'Spicy Thai Basil Stir-Fry',
      description: 'Quick and flavorful stir-fry with fresh vegetables, aromatic Thai basil, and your choice of protein in a savory-spicy sauce.',
      thumbnail: '🍜',
      prepTime: '20 min',
      difficulty: 'Medium'
    }, {
      id: '3',
      title: 'Creamy Mushroom Risotto',
      description: 'Classic Italian comfort food with arborio rice, mixed mushrooms, parmesan, and a touch of white wine. Rich and satisfying.',
      thumbnail: '🍄',
      prepTime: '40 min',
      difficulty: 'Medium'
    }, {
      id: '4',
      title: 'Grilled Salmon with Herb Butter',
      description: 'Perfectly grilled salmon fillet topped with compound herb butter, served with roasted asparagus and lemon wedges.',
      thumbnail: '🐟',
      prepTime: '30 min',
      difficulty: 'Easy'
    }, {
      id: '5',
      title: 'Vegetarian Taco Bowl',
      description: 'A colorful bowl with seasoned black beans, corn, avocado, fresh salsa, and cilantro-lime rice. Customizable and crowd-pleasing.',
      thumbnail: '🌮',
      prepTime: '25 min',
      difficulty: 'Easy'
    }];
    const options = allOptions.slice(0, count);
    const message = userMessage ? `I've refined the options based on your request: "${userMessage}". These ${count} meals balance your group's dietary needs while maintaining variety and appeal.` : `Based on your group preferences and dietary requirements, I've generated ${count} meal concepts that should work well. Each option respects the critical requirements while incorporating the flavor profiles and styles your guests enjoy. Select one to see the full recipe, or chat with me to refine these options.`;
    return {
      options,
      message
    };
  };
  const generateMockRecipe = (optionId: string): DetailedRecipe => {
    const option = currentOptions?.find(o => o.id === optionId);
    return {
      id: optionId,
      thumbnail: option?.thumbnail || '🍽️',
      title: option?.title || 'Generated Recipe',
      rating: 4,
      description: option?.description || 'A delicious meal tailored to your preferences.',
      prepTime: option?.prepTime || '30 min',
      cookTime: '25 min',
      difficulty: option?.difficulty || 'Medium',
      createdBy: userProfile?.name || 'AI Chef',
      createdOn: new Date().toLocaleDateString(),
      defaultServings: guestCount,
      ingredients: [{
        item: 'olive oil',
        amount: 2,
        unit: 'tbsp'
      }, {
        item: 'garlic cloves, minced',
        amount: 3,
        unit: ''
      }, {
        item: 'onion, diced',
        amount: 1,
        unit: ''
      }, {
        item: 'main protein or base',
        amount: 500,
        unit: 'g'
      }, {
        item: 'vegetable stock',
        amount: 2,
        unit: 'cups'
      }, {
        item: 'fresh herbs',
        amount: 0.25,
        unit: 'cup'
      }, {
        item: 'salt',
        amount: 1,
        unit: 'tsp'
      }, {
        item: 'black pepper',
        amount: 0.5,
        unit: 'tsp'
      }],
      instructions: ['Prepare all ingredients by washing, chopping, and measuring according to the ingredient list above.', 'Heat olive oil in a large pan over medium-high heat. Add minced garlic and diced onion, sauté until fragrant and translucent (about 3-4 minutes).', 'Add the main protein or base ingredient to the pan. Cook until properly seared or softened, stirring occasionally.', 'Pour in the vegetable stock and bring to a gentle simmer. Reduce heat to medium-low.', 'Season with salt and pepper to taste. Allow to cook for 15-20 minutes, stirring occasionally, until flavors meld and liquid reduces slightly.', 'Add fresh herbs in the final 2 minutes of cooking. Stir well to incorporate.', 'Remove from heat and let rest for 2-3 minutes before serving. Garnish with additional fresh herbs if desired.'],
      storage: 'Store in an airtight container in the refrigerator for up to 3-4 days. Allow to cool completely before refrigerating. Can be frozen for up to 2 months in freezer-safe containers.',
      reheating: 'Reheat in a covered pan over medium heat with a splash of water or stock to prevent drying. Microwave option: heat in 1-minute intervals, stirring between, until warmed through. Oven: reheat at 350°F (175°C) for 15-20 minutes covered.'
    };
  };
  const generateOptions = async (data: RecipeFormData) => {
    setIsLoading(true);
    setCurrentOptions(null);
    setAgentMessage(null);
    setSelectedOptionId(null);
    setCurrentRecipe(null);
    setGuestCount(data.guests.length);
    setCurrentOptionCount(data.optionCount);
    await new Promise(resolve => setTimeout(resolve, 2500));
    const {
      options,
      message
    } = generateMockOptions(data.optionCount);
    const newSet: OptionSet = {
      id: Date.now().toString(),
      options,
      agentMessage: message,
      timestamp: new Date()
    };
    setCurrentOptions(options);
    setAgentMessage(message);
    setOptionHistory(prev => [...prev, newSet]);
    setIsLoading(false);
    setMobileStep(4); // Move to options step on mobile
  };
  const handleGenerateMoreOptions = async () => {
    setIsLoading(true);
    setSelectedOptionId(null);
    await new Promise(resolve => setTimeout(resolve, 2000));
    const {
      options: newOptions,
      message
    } = generateMockOptions(currentOptionCount, 'generate more options');
    const combinedOptions = [...(currentOptions || []), ...newOptions];
    const newSet: OptionSet = {
      id: Date.now().toString(),
      options: combinedOptions,
      agentMessage: message,
      timestamp: new Date()
    };
    setCurrentOptions(combinedOptions);
    setAgentMessage(message);
    setOptionHistory(prev => [...prev, newSet]);
    setIsLoading(false);
  };
  const handleSendMessage = async (userMessage: string) => {
    setIsLoading(true);
    setSelectedOptionId(null);
    setCurrentRecipe(null);
    await new Promise(resolve => setTimeout(resolve, 2000));
    const {
      options,
      message
    } = generateMockOptions(currentOptionCount, userMessage);
    const newSet: OptionSet = {
      id: Date.now().toString(),
      options,
      agentMessage: message,
      timestamp: new Date()
    };
    setCurrentOptions(options);
    setAgentMessage(message);
    setOptionHistory(prev => [...prev, newSet]);
    setIsLoading(false);
  };
  const handleSelectOption = (optionId: string) => {
    setSelectedOptionId(selectedOptionId === optionId ? null : optionId);
  };
  const handleGenerateRecipe = async () => {
    if (!selectedOptionId) return;
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    const recipe = generateMockRecipe(selectedOptionId);
    setCurrentRecipe(recipe);
    setIsLoading(false);
    setMobileStep(5); // Move to recipe step on mobile
  };
  const handleViewHistory = (setId: string) => {
    const historicalSet = optionHistory.find(set => set.id === setId);
    if (historicalSet) {
      setCurrentOptions(historicalSet.options);
      setAgentMessage(historicalSet.agentMessage);
      setSelectedOptionId(null);
      setCurrentRecipe(null);
    }
  };
  const handleDiscardRecipe = () => {
    setCurrentRecipe(null);
    setMobileStep(4); // Go back to options on mobile
  };
  const handleSaveRecipe = () => {
    alert('Recipe saved! This would navigate to a published/saved recipe view.');
  };
  const handleFinishRecipe = () => {
    // Navigate to recipe viewer with the current recipe
    if (onBackToHome && currentRecipe) {
      onBackToHome();
    }
  };
  const handleRequestRevision = async (message: string) => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    if (selectedOptionId) {
      const revisedRecipe = generateMockRecipe(selectedOptionId);
      setCurrentRecipe(revisedRecipe);
    }
    setIsLoading(false);
  };
  const handleSendRevision = () => {
    if (chatInput.trim()) {
      handleRequestRevision(chatInput);
      setChatInput('');
    }
  };
  const handleSendOptionsMessage = () => {
    if (optionsChatInput.trim()) {
      handleSendMessage(optionsChatInput);
      setOptionsChatInput('');
    }
  };
  const totalSteps = 5;
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933] font-sans" data-id="element-1050">
      {/* Header Section */}
      <header className="bg-white border-b-2 border-gray-200 px-4 md:px-8 py-4 md:mb-8" data-id="element-1051">
        <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-1052">
          <div className="flex items-center space-x-4" data-id="element-1053">
            <NavigationDropdown onNavigateHome={onNavigateHome} onGenerateRecipe={onGenerateRecipe} onImportRecipe={onImportRecipe} onBrowseRecipes={onBrowseRecipes} onManageGuests={onManageGuests} defaultAction="home" data-id="element-1054" />
            <div className="hidden md:block" data-id="element-1055">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-1056">
                Recipe Builder
              </h1>
              <p className="text-sm text-gray-500 font-mono" data-id="element-1057">
                Generate custom recipes for your guests
              </p>
            </div>
          </div>

          <button onClick={onEditProfile} className="flex items-center space-x-2 px-3 py-2 border-2 border-gray-300 hover:border-gray-500 transition-colors bg-white flex-shrink-0" data-id="element-1058">
            <div className="w-6 h-6 border border-gray-300 flex items-center justify-center text-sm" data-id="element-1059">
              {userProfile?.avatar || <User className="w-3 h-3 text-gray-400" data-id="element-1060" />}
            </div>
            <span className="font-mono text-xs text-gray-600 hidden sm:inline" data-id="element-1061">
              {userProfile?.name || 'User'}
            </span>
          </button>
        </div>
      </header>

      {/* Mobile Title - Below Header */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 mb-8" data-id="element-1062">
        <h1 className="text-xl font-bold text-gray-900" data-id="element-1063">Recipe Builder</h1>
        <p className="text-xs text-gray-500 font-mono" data-id="element-1064">
          Generate custom recipes for your guests
        </p>
      </div>

      {/* Mobile Progress Tracker - Simple dots like onboarding */}
      <div className="lg:hidden px-4 mb-8" data-id="element-1065">
        <div className="flex items-center justify-between border-b border-gray-200 pb-4" data-id="element-1066">
          <div className="flex space-x-2" data-id="element-1067">
            {[1, 2, 3, 4, 5].map(s => <div key={s} className={`h-2 w-8 rounded-full transition-colors ${s <= mobileStep ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-1068" />)}
          </div>
          <span className="font-mono text-xs text-gray-400 uppercase tracking-widest" data-id="element-1069">
            Step 0{mobileStep} / 0{totalSteps}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <main className="px-4 md:px-8 pb-8" data-id="element-1070">
        <div className="max-w-5xl mx-auto" data-id="element-1071">
          {/* Desktop: Side-by-side layout (unchanged) */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-12 items-start" data-id="element-1072">
            {/* Left Column: Input Controls */}
            <div className="lg:col-span-5 space-y-4" data-id="element-1073">
              <div className="flex items-center justify-between mb-2" data-id="element-1074">
                <h2 className="font-mono text-xs uppercase tracking-widest text-gray-400" data-id="element-1075">
                  Input Parameters
                </h2>
                <div className="h-px flex-1 bg-gray-200 ml-4" data-id="element-1076"></div>
              </div>
              <RecipeForm onSubmit={generateOptions} isLoading={isLoading} mobileStep={null} onMobileStepChange={() => {}} data-id="element-1077" />

              <div className="p-4 border border-gray-200 bg-white/30 text-xs font-mono text-gray-400 space-y-2" data-id="element-1078">
                <p data-id="element-1079">
                  NOTE: Results are generated based on probabilistic models.
                </p>
                <p data-id="element-1080">Refine options through the chat interface below.</p>
              </div>
            </div>

            {/* Right Column: Output Display */}
            <div className="lg:col-span-7 space-y-4" data-id="element-1081">
              <div className="flex items-center justify-between mb-2" data-id="element-1082">
                <h2 className="font-mono text-xs uppercase tracking-widest text-gray-400" data-id="element-1083">
                  Output Schematic
                </h2>
                <div className="h-px flex-1 bg-gray-200 ml-4" data-id="element-1084"></div>
              </div>

              {currentRecipe ? <>
                  <RecipeDetailView recipe={currentRecipe} onDiscard={handleDiscardRecipe} onSave={handleSaveRecipe} isLoading={isLoading} onFinish={handleFinishRecipe} showFinishButton={true} data-id="element-1085" />

                  {/* Chat Interface - Separate from recipe */}
                  <div className="border-2 border-gray-300 bg-white p-4" data-id="element-1086">
                    <h3 className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-3" data-id="element-1087">
                      Request Revisions
                    </h3>
                    <div className="flex gap-2" data-id="element-1088">
                      <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendRevision()} placeholder="Request changes to the recipe..." disabled={isLoading} className="flex-1 border-2 border-gray-300 bg-white px-3 py-2 font-mono text-xs focus:border-gray-500 focus:outline-none placeholder:text-gray-300 disabled:opacity-50" data-id="element-1089" />

                      <button onClick={handleSendRevision} disabled={!chatInput.trim() || isLoading} className="px-4 py-2 bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center" data-id="element-1090">
                        {isLoading ? <span className="font-mono text-xs" data-id="element-1091">...</span> : <Send className="w-3 h-3" data-id="element-1092" />}
                      </button>
                    </div>
                    <p className="text-xs font-mono text-gray-400 mt-2" data-id="element-1093">
                      Describe modifications to regenerate this recipe
                    </p>
                  </div>
                </> : <>
                  <MealOptionsDisplay currentOptions={currentOptions} agentMessage={agentMessage} isLoading={isLoading} onSelectOption={handleSelectOption} selectedOptionId={selectedOptionId} onGenerateRecipe={handleGenerateRecipe} onGenerateMoreOptions={handleGenerateMoreOptions} optionHistory={optionHistory} onViewHistory={handleViewHistory} data-id="element-1094" />

                  {/* Chat Interface - Separate from options */}
                  {currentOptions && <div className="border-2 border-gray-300 bg-white p-4" data-id="element-1095">
                      <h3 className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-3" data-id="element-1096">
                        Refine Options
                      </h3>
                      <div className="flex gap-2" data-id="element-1097">
                        <input type="text" value={optionsChatInput} onChange={e => setOptionsChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendOptionsMessage()} placeholder="Refine options... (e.g., 'Make them vegetarian')" disabled={isLoading} className="flex-1 border-2 border-gray-300 bg-white px-3 py-2 font-mono text-xs focus:border-gray-500 focus:outline-none placeholder:text-gray-300 disabled:opacity-50" data-id="element-1098" />

                        <button onClick={handleSendOptionsMessage} disabled={!optionsChatInput.trim() || isLoading} className="px-4 py-2 bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center" data-id="element-1099">
                          {isLoading ? <span className="font-mono text-xs" data-id="element-1100">...</span> : <Send className="w-3 h-3" data-id="element-1101" />}
                        </button>
                      </div>
                      <p className="text-xs font-mono text-gray-400 mt-2" data-id="element-1102">
                        Request changes to the meal options
                      </p>
                    </div>}
                </>}
            </div>
          </div>

          {/* Mobile: Step-by-step guided experience */}
          <div className="lg:hidden space-y-6" data-id="element-1103">
            {/* Form Steps (1-3) */}
            {mobileStep <= 3 && <RecipeForm onSubmit={generateOptions} isLoading={isLoading} mobileStep={mobileStep} onMobileStepChange={setMobileStep} data-id="element-1104" />}

            {/* Options Step (4) */}
            {mobileStep === 4 && <div className="space-y-4" data-id="element-1105">
                <MealOptionsDisplay currentOptions={currentOptions} agentMessage={agentMessage} isLoading={isLoading} onSelectOption={handleSelectOption} selectedOptionId={selectedOptionId} onGenerateRecipe={handleGenerateRecipe} onGenerateMoreOptions={handleGenerateMoreOptions} optionHistory={optionHistory} onViewHistory={handleViewHistory} data-id="element-1106" />

                {/* Chat Interface - Separate on mobile */}
                {currentOptions && <div className="border-2 border-gray-300 bg-white p-4" data-id="element-1107">
                    <h3 className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-3" data-id="element-1108">
                      Refine Options
                    </h3>
                    <div className="flex gap-2" data-id="element-1109">
                      <input type="text" value={optionsChatInput} onChange={e => setOptionsChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendOptionsMessage()} placeholder="Refine options..." disabled={isLoading} className="flex-1 border-2 border-gray-300 bg-white px-3 py-2 font-mono text-xs focus:border-gray-500 focus:outline-none placeholder:text-gray-300 disabled:opacity-50" data-id="element-1110" />

                      <button onClick={handleSendOptionsMessage} disabled={!optionsChatInput.trim() || isLoading} className="px-4 py-2 bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center" data-id="element-1111">
                        {isLoading ? <span className="font-mono text-xs" data-id="element-1112">...</span> : <Send className="w-3 h-3" data-id="element-1113" />}
                      </button>
                    </div>
                    <p className="text-xs font-mono text-gray-400 mt-2" data-id="element-1114">
                      Request changes to meal options
                    </p>
                  </div>}

                <button onClick={() => setMobileStep(1)} className="w-full py-3 border-2 border-gray-300 hover:border-gray-500 transition-colors font-mono text-sm uppercase tracking-widest flex items-center justify-center" data-id="element-1115">
                  <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1116" />
                  Start Over
                </button>
              </div>}

            {/* Recipe Step (5) */}
            {mobileStep === 5 && currentRecipe && <div className="space-y-4" data-id="element-1117">
                <RecipeDetailView recipe={currentRecipe} onDiscard={handleDiscardRecipe} onSave={handleSaveRecipe} isLoading={isLoading} onFinish={handleFinishRecipe} showFinishButton={true} data-id="element-1118" />

                {/* Chat Interface - Separate from recipe on mobile */}
                <div className="border-2 border-gray-300 bg-white p-4" data-id="element-1119">
                  <h3 className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-3" data-id="element-1120">
                    Request Revisions
                  </h3>
                  <div className="flex gap-2" data-id="element-1121">
                    <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendRevision()} placeholder="Request changes..." disabled={isLoading} className="flex-1 border-2 border-gray-300 bg-white px-3 py-2 font-mono text-xs focus:border-gray-500 focus:outline-none placeholder:text-gray-300 disabled:opacity-50" data-id="element-1122" />

                    <button onClick={handleSendRevision} disabled={!chatInput.trim() || isLoading} className="px-4 py-2 bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center" data-id="element-1123">
                      {isLoading ? <span className="font-mono text-xs" data-id="element-1124">...</span> : <Send className="w-3 h-3" data-id="element-1125" />}
                    </button>
                  </div>
                  <p className="text-xs font-mono text-gray-400 mt-2" data-id="element-1126">
                    Describe modifications to regenerate
                  </p>
                </div>

                <div className="space-y-3" data-id="element-1127">
                  <button onClick={handleFinishRecipe} className="w-full py-4 bg-gray-900 text-white font-mono text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors" data-id="element-1128">
                    Finish & Publish Recipe
                  </button>

                  <button onClick={() => setMobileStep(4)} className="w-full py-3 border-2 border-gray-300 hover:border-gray-500 transition-colors font-mono text-sm uppercase tracking-widest flex items-center justify-center" data-id="element-1129">
                    <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1130" />
                    Back to Options
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="pt-12 pb-4 text-center border-t border-gray-200" data-id="element-1131">
        <p className="font-mono text-xs text-gray-400" data-id="element-1132">
          © {new Date().getFullYear()} AI RECIPE ARCHITECT // WIREFRAME MODE
        </p>
      </footer>
    </div>;
}